<template>
  <div
    class="page-viewer"
    :style="[pageBackgroundCss, computedStyle]"
  >
    <div
      ref="pagePreview"
      class="screen-height"
      :style="pageScaleCss"
    >
      <component
        :is="widgetsByType[w.type].view"
        v-for="w in widgets"
        :key="w.id"
        :widget="w"
        :data-uuid="w.id"
        @videoLoaded="videoLoaded"
        @userInteraction="data => $emit('userInteraction', data)"
      />
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import consts from './consts';
import availableWidgets from './widgets/available-widgets';

export default {
  name: 'PageViewer',
  components: {
  },
  props: {
    pageContent: {
      type: Object,
      required: true,
    },
    shareMode: {
      type: Boolean,
      default: () => false,
    },
    selectedWidget: {
      type: Object,
    },
    pageId: {
      type: Number,
    },
    scale: {
      type: Number,
      default: 1,
    },
    ratio: {
      type: String,
      default: '100%',
    },
  },
  data() {
    return {
      startX: 0,
      startY: 0,
      originX: 0,
      originY: 0,
      movingWidget: null,
      pageHeight: 0,
      pageWidth: 0,
    };
  },
  computed: {
    widgets() {
      return this.pageContent.widgets;
    },
    widgetsByType() {
      return availableWidgets();
    },
    selectedWidgetInPage() {
      return _.find(this.widgets, w => w.id === this.selectedWidget?.id);
    },
    selectedWidgetIsSizable() {
      return this.selectedWidget
          && this.selectedWidgetInPage
          && _.find(this.widgets, w => w.id === this.selectedWidget.id)
          && _.has(this.selectedWidget, 'width')
          && _.has(this.selectedWidget, 'height');
    },
    pageScale() {
      const screenWidthRatio = this.pageWidth / consts.BASE_SCREEN_WIDTH;
      return screenWidthRatio;
    },
    pageScaleCss() {
      if (this.shareMode) {
        return {};
      }

      const translateX = (this.pageWidth - consts.BASE_SCREEN_WIDTH) / 2;
      // Determine vertical position: align to top (0) or center vertically on the page
      // This allows content to either anchor to the top or stay centered during scaling
      const translateY = 0;
      return {
        transform: `scale(${this.pageScale}) translate(${translateX}px, ${translateY}px)`,
      };
    },
    pageBackgroundCss() {
      const backgroundColor = this.pageContent.background;
      return backgroundColor ? { background: backgroundColor } : {};
    },
    computedStyle() {
      return {
        height: this.ratio.match(/^\d+px$/) ? this.ratio : '100%',
      };
    },
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize);
  },
  mounted() {
    this.onResize();
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResize);
    });
  },
  methods: {
    onResize() {
      // On desktop (page width greater than 430 we calculated the size of the page differently
      // This is not optimal since it duplicate logic in different place in the app, we decided to live with this duplication
      // instead of starting to extract it at the moment
      if (window.innerWidth >= 430) {
        this.pageWidth = 400;
        this.pageHeight = 0.9 * window.innerHeight - 35 - 20;
      } else {
        this.pageWidth = window.innerWidth;
        this.pageHeight = window.innerHeight;
      }
    },
    handleMouseMove(e) {
      e.stopPropagation();
      e.preventDefault();

      const dx = (e.pageX - this.startX) / this.scale;
      const dy = (e.pageY - this.startY) / this.scale;
      const left = this.originX + dx;
      const top = this.originY + dy;

      this.movingWidget.left = left;
      this.movingWidget.top = top;
    },
    handleMouseUp() {
      document.removeEventListener('mousemove', this.handleMouseMove, true);
      document.removeEventListener('mouseup', this.handleMouseUp, true);
      this.movingWidget = null;
    },
    findSelectedWidget(el) {
      if (el === this.$refs.pagePreview) {
        return null;
      }
      const uuid = el.getAttribute('data-uuid');
      if (uuid) {
        return _.find(this.widgets, w => w.id === uuid);
      }
      return this.findSelectedWidget(el.parentElement);
    },
    videoLoaded(videoRef) {
      this.$emit('videoLoaded', { ref: videoRef, pageId: this.pageId });
    },
  },
};
</script>

<style lang="scss" scoped>
.page-viewer {
  position: relative;
  height: 100%;

  .screen-height {
    width: 100%;
  }

  .text-component {
    position: absolute;
  }

  .widget-view:hover {
    outline: 1px solid #ddd;
  }
}
</style>
